import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Divider, 
  Avatar, 
  Stack, 
  Grid, 
  Paper, 
  IconButton, 
  Chip,
  Fade,
  Zoom,
  Input,
  InputAdornment,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { LeagueManagementService } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBack, Sports, CalendarToday, LocationOn, Flag, Search, Clear, FilterList } from "@mui/icons-material";
import { fDate } from "../../utils/formatTime";
import { Helmet } from "react-helmet-async";
import "./ViewLeagueManagement.css";

const ViewAddEditLeagueManagement = () => {
  const { id } = useParams();
  const [data, setData] = useState({});
  const [AllMatch, setAllMatch] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [matchesPerPage, setMatchesPerPage] = useState(6);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await LeagueManagementService.getById({ id });
        setData(response?.data?.data?.league);
        setAllMatch(response?.data?.data?.matchData);
        setFilteredMatches(response?.data?.data?.matchData || []);
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchData();
  }, [id]);

  // Filter matches based on search term and date range
  const filteredMatchesData = useMemo(() => {
    let filtered = AllMatch;

    // Filter by team name search
    if (searchTerm) {
      filtered = filtered.filter(match => {
        const [team1, team2] = match.participants || [];
        const team1Name = team1?.name?.toLowerCase() || '';
        const team2Name = team2?.name?.toLowerCase() || '';
        const searchLower = searchTerm.toLowerCase();
        
        return team1Name.includes(searchLower) || team2Name.includes(searchLower);
      });
    }

    // Filter by date range
    if (startDate && endDate) {
      filtered = filtered.filter(match => {
        if (!match.startingAt) return false;
        const matchDate = new Date(match.startingAt);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return matchDate >= start && matchDate <= end;
      });
    } else if (dateFilter) {
      const today = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(match => {
            if (!match.startingAt) return false;
            const matchDate = new Date(match.startingAt);
            return matchDate.toDateString() === today.toDateString();
          });
          break;
        case 'week':
          filterDate.setDate(today.getDate() + 7);
          filtered = filtered.filter(match => {
            if (!match.startingAt) return false;
            const matchDate = new Date(match.startingAt);
            return matchDate >= today && matchDate <= filterDate;
          });
          break;
        case 'month':
          filterDate.setMonth(today.getMonth() + 1);
          filtered = filtered.filter(match => {
            if (!match.startingAt) return false;
            const matchDate = new Date(match.startingAt);
            return matchDate >= today && matchDate <= filterDate;
          });
          break;
        case 'past':
          filtered = filtered.filter(match => {
            if (!match.startingAt) return false;
            const matchDate = new Date(match.startingAt);
            return matchDate < today;
          });
          break;
        default:
          break;
      }
    }

    return filtered;
  }, [AllMatch, searchTerm, startDate, endDate, dateFilter]);

  // Update filtered matches when filter data changes
  useEffect(() => {
    setFilteredMatches(filteredMatchesData);
  }, [filteredMatchesData]);

  const clearFilters = () => {
    setSearchTerm("");
    setDateFilter("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredMatches.length / matchesPerPage);
  const startIndex = (currentPage - 1) * matchesPerPage;
  const endIndex = startIndex + matchesPerPage;
  const currentMatches = filteredMatches.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setMatchesPerPage(size);
    setCurrentPage(1);
  };

  return (
    <Box className="view-league-container">
      <Helmet>
        <title>{data?.name || 'League'} - League Details</title>
      </Helmet>
      <Fade in timeout={500}>
        <Paper className="view-league-paper view-league-fade-in">
          {/* Header Section */}
          <Box className="view-league-header">
            <IconButton 
              onClick={() => navigate(-1)}
              className="view-league-back-button"
            >
          <ArrowBack />
        </IconButton>

            <Box className="view-league-logo-container">
              <Box className="view-league-logo-wrapper">
            <img
              src={data.logo}
              alt={data?.name || 'League Logo'}
                  className="view-league-logo"
            />
          </Box>
              <Typography variant="h3" className="view-league-title">
            {data?.name || "N/A"}
          </Typography>
              <Typography variant="h6" className="view-league-subtitle">
                {data?.country || "International League"}
              </Typography>
            </Box>
          </Box>

          {/* Content Section */}
          <Box className="view-league-content">
            {/* League Details Section */}
            <Box className="view-league-section">
              <Typography variant="h4" className="view-league-section-title">
                League Information
              </Typography>
              
              <Box className="view-league-details-grid">
                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    <Sports sx={{ mr: 1, verticalAlign: 'middle' }} />
                    League ID
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.leagueId || "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    <Sports sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Sport ID
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.sportId || "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Country
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.country || "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    <Flag sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Country ID
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.countryId || "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    Type
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.type || "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    Sub Type
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.subType || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider className="view-league-divider" />

            {/* Current Season Section */}
            <Box className="view-league-section">
              <Typography variant="h4" className="view-league-section-title">
                Current Season
              </Typography>
              
              <Box className="view-league-details-grid">
                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    Season Name
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.currentSeason?.name || "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    Season ID
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.currentSeason?.seasonId || "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Starting At
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.currentSeason?.startingAt ? fDate(data.currentSeason.startingAt) : "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Ending At
                  </Typography>
                  <Typography className="view-league-detail-value">
                    {data?.currentSeason?.endingAt ? fDate(data.currentSeason.endingAt) : "N/A"}
                  </Typography>
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    Status
                  </Typography>
                  <Chip
                    label={data?.currentSeason?.finished ? "Finished" : "Ongoing"}
                    className={`view-league-status-badge ${
                      data?.currentSeason?.finished ? 'view-league-status-badge-inactive' : 'view-league-status-badge-active'
                    }`}
                  />
                </Box>

                <Box className="view-league-detail-card">
                  <Typography className="view-league-detail-label">
                    Pending
                  </Typography>
                  <Chip
                    label={data?.currentSeason?.pending ? "Yes" : "No"}
                    className={`view-league-status-badge ${
                      data?.currentSeason?.pending ? 'view-league-status-badge-inactive' : 'view-league-status-badge-active'
                    }`}
                  />
                </Box>
              </Box>
            </Box>

            <Divider className="view-league-divider" />

            {/* Status Section */}
            <Box className="view-league-status-section">
              <Box className="view-league-status-item">
                <Typography className="view-league-status-label">
                  Last Played At:
                </Typography>
                <Typography className="view-league-status-value">
                  {data?.lastPlayedAt ? fDate(data.lastPlayedAt) : "N/A"}
                </Typography>
              </Box>
              
              <Box className="view-league-status-item">
                <Typography className="view-league-status-label">
                  League Status:
        </Typography>
                <Chip
                  label={data?.status ? "Active" : "Inactive"}
                  className={`view-league-status-badge ${
                    data?.status ? 'view-league-status-badge-active' : 'view-league-status-badge-inactive'
                  }`}
                />
              </Box>
            </Box>

            <Divider className="view-league-divider" />

            {/* Matches Section */}
            <Box className="view-league-matches-container">
              <Typography variant="h4" className="view-league-section-title">
          All Matches
        </Typography>

              {/* Filter Controls */}
              <Box className="view-league-filters-container">
                <Box className="view-league-filters-header">
                  <Box className="view-league-filters-title">
                    <FilterList className="view-league-filter-icon" />
                    <Typography variant="h6" className="view-league-filters-title-text">
                      Filter Matches
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={3} alignItems="flex-end" sx={{ mt: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Input
                      placeholder="Search by team name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="view-league-search-input"
                      startAdornment={
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      }
                      endAdornment={
                        searchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchTerm("")}
                              className="view-league-clear-search"
                            >
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <FormControl fullWidth className="view-league-date-filter">
                      <InputLabel>Quick Date Filter</InputLabel>
                      <Select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        label="Quick Date Filter"
                      >
                        <MenuItem value="">All Matches</MenuItem>
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="week">Next 7 Days</MenuItem>
                        <MenuItem value="month">Next Month</MenuItem>
                        <MenuItem value="past">Past Matches</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box className="view-league-date-range">
                      <TextField
                        type="date"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        className="view-league-date-input"
                        fullWidth
                      />
                      <TextField
                        type="date"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        className="view-league-date-input"
                        fullWidth
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <Button
                      variant="outlined"
                      onClick={clearFilters}
                      startIcon={<Clear />}
                      className="view-league-clear-filters"
                      fullWidth
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>

                {/* Filter Results Summary */}
                {(searchTerm || dateFilter || startDate || endDate) && (
                  <Box className="view-league-filter-summary">
                    <Typography variant="body2" className="view-league-filter-text">
                      Showing {filteredMatches.length} of {AllMatch.length} matches
                      {searchTerm && ` matching "${searchTerm}"`}
                      {dateFilter && ` in ${dateFilter}`}
                      {startDate && endDate && ` from ${fDate(startDate)} to ${fDate(endDate)}`}
                    </Typography>
                  </Box>
                )}
              </Box>

              {filteredMatches.length > 0 ? (
                <>
                  <Box className="view-league-matches-grid">
                    {currentMatches.map((match, index) => {
            const [team1, team2] = match.participants || [];
            return (
                      <Zoom 
                        in 
                        timeout={300 + (index * 100)} 
                        key={index}
                      >
              <Card
                          className="view-league-match-card view-league-slide-up"
                onClick={() => {
                  // optional: navigate to match detail page
                }}
              >
                          <Box className="view-league-match-teams">
                            <Box className="view-league-team">
                    <Avatar
                      src={team1?.imagePath}
                      alt={team1?.name}
                                className="view-league-team-avatar"
                              />
                              <Typography className="view-league-team-name">
                      {team1?.name || "Team 1"}
                    </Typography>
                            </Box>

                            <Typography className="view-league-vs-text">
                    vs
                  </Typography>

                            <Box className="view-league-team view-league-team-away">
                              <Typography className="view-league-team-name">
                                {team2?.name || "Team 2"}
                              </Typography>
                    <Avatar
                      src={team2?.imagePath}
                      alt={team2?.name}
                                className="view-league-team-avatar"
                              />
                            </Box>
                          </Box>

                          <Box className="view-league-match-details">
                            <Typography className="view-league-match-datetime">
                    {match.startingAt
                                ? fDate(match.startingAt)
                      : "TBD"}
                  </Typography>
                </Box>
              </Card>
                      </Zoom>
                    );
                  })}
                  </Box>

                  {/* Pagination Controls */}
                  <Box className="view-league-pagination-container">
                    <Box className="view-league-pagination-info">
                      <Typography variant="body2" className="view-league-pagination-text">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredMatches.length)} of {filteredMatches.length} matches
                      </Typography>
                    </Box>
                    
                    <Box className="view-league-pagination-controls">
                      <Box className="view-league-page-size-control">
                        <Typography variant="body2" className="view-league-page-size-label">
                          Per page:
                        </Typography>
                        <FormControl size="small" className="view-league-page-size-select">
                          <Select
                            value={matchesPerPage}
                            onChange={(e) => handlePageSizeChange(e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={6}>6</MenuItem>
                            <MenuItem value={8}>8</MenuItem>
                            <MenuItem value={12}>12</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box className="view-league-page-buttons">
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                          className="view-league-page-button"
                        >
                          Previous
                        </Button>
                        
                        <Box className="view-league-page-numbers">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "contained" : "outlined"}
                                size="small"
                                onClick={() => handlePageChange(pageNum)}
                                className={`view-league-page-number ${currentPage === pageNum ? 'active' : ''}`}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </Box>

                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage === totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                          className="view-league-page-button"
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box className="view-league-empty-matches">
                  <Typography variant="h6">
                    {AllMatch.length === 0 
                      ? "No matches found for this league" 
                      : "No matches match your current filters"
                    }
                  </Typography>
                  <Typography variant="body2">
                    {AllMatch.length === 0 
                      ? "Check back later for upcoming matches"
                      : "Try adjusting your search criteria or clear filters"
                    }
          </Typography>
                  {AllMatch.length > 0 && (
                    <Button
                      variant="outlined"
                      onClick={clearFilters}
                      startIcon={<Clear />}
                      className="view-league-clear-filters-btn"
                      sx={{ mt: 2 }}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
      </Paper>
      </Fade>
    </Box>
  );
};

export default ViewAddEditLeagueManagement;